import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// useFailedPaymentAlerts — pastille « paiement échoué » du back-office.
//
// Dès qu'une tentative échoue (FeexPay, Stripe, PayPal…), le journal
// `payment_attempt_logs` reçoit une ligne et émet l'événement
// `zifek:payment-failed`. Ce hook fournit :
//  - `count`  : nombre d'échecs sur les dernières 24 h (badge),
//  - `lastId` : identifiant de la dernière ligne (lien direct dans le journal),
//  - `last`   : la dernière tentative complète (fournisseur + message).
// ─────────────────────────────────────────────────────────────

export interface LastFailedPayment {
  id: number;
  provider: string;
  error_message: string | null;
  operator: string | null;
  created_at: string;
}

export default function useFailedPaymentAlerts(pollMs = 60000, enabled = true) {
  const [count, setCount] = useState(0);
  const [last, setLast] = useState<LastFailedPayment | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // 🚫 Pas de requête avant authentification : évite les 401 inutiles
    // sur les pages publiques et pendant le chargement de session.
    if (!enabled) {
      setLoading(false);
      return;
    }
    try {
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from('payment_attempt_logs')
        .select('id, provider, error_message, operator, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      const rows = (data || []) as LastFailedPayment[];
      setCount(rows.length);
      setLast(rows[0] || null);
    } catch {
      /* la pastille ne doit jamais casser le back-office */
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void refresh();
    const onFail = () => void refresh();
    window.addEventListener('zifek:payment-failed', onFail);
    const timer = window.setInterval(() => void refresh(), pollMs);
    return () => {
      window.removeEventListener('zifek:payment-failed', onFail);
      window.clearInterval(timer);
    };
  }, [refresh, pollMs]);

  return { count, lastId: last?.id ?? null, last, loading, refresh };
}